<div class="topbar">
  <div class="brand">
    <button type="button" class="backBtn" (click)="goBack()" aria-label="Go back">&#8592;</button>
    <img src="assets/images/dairector-logo.svg" alt="dAIrector" class="logo" />
  </div>
  <span class="tag">User Guide &middot; v{{ version }}</span>
  <button class="menuBtn" type="button" (click)="toggleToc()" aria-label="Toggle contents">&#9776;</button>
</div>

<div class="shell">
  <nav class="toc" id="toc" [class.open]="tocOpen">
    <h4>Start here</h4>
    <a href="#intro" (click)="scrollTo($event, 'intro')">What dAIrector is</a>
    <a href="#signin" (click)="scrollTo($event, 'signin')">Signing in</a>
    <a href="#firstrun" (click)="scrollTo($event, 'firstrun')">First time you log in</a>
    <a href="#command" (click)="scrollTo($event, 'command')">The Command Centre</a>
    <a href="#twoways" (click)="scrollTo($event, 'twoways')">Two ways to create</a>

    <h4>Campaign pipeline</h4>
    <a href="#hub" (click)="scrollTo($event, 'hub')">Campaign Hub</a>
    <a href="#details" (click)="scrollTo($event, 'details')">Inside a campaign</a>
    <a href="#s-manifest" class="step" (click)="scrollTo($event, 's-manifest')"><span class="n">1</span>Concept Manifest</a>
    <a href="#s-script" class="step" (click)="scrollTo($event, 's-script')"><span class="n">2</span>Script</a>
    <a href="#s-moodboard" class="step" (click)="scrollTo($event, 's-moodboard')"><span class="n">3</span>Moodboard</a>
    <a href="#s-storyboard" class="step" (click)="scrollTo($event, 's-storyboard')"><span class="n">4</span>Storyboard</a>
    <a href="#s-video" class="step" (click)="scrollTo($event, 's-video')"><span class="n">5</span>Video</a>
    <a href="#s-sound" class="step" (click)="scrollTo($event, 's-sound')"><span class="n">6</span>Sound</a>
    <a href="#s-preview" class="step" (click)="scrollTo($event, 's-preview')"><span class="n">7</span>Preview &amp; deliver</a>

    <h4>Quick Create</h4>
    <a href="#library" (click)="scrollTo($event, 'library')">Asset Library</a>
    <a href="#builders" (click)="scrollTo($event, 'builders')">The six builders</a>

    <h4>Account</h4>
    <a href="#organisation" (click)="scrollTo($event, 'organisation')">Organisation</a>
    <a href="#credits" (click)="scrollTo($event, 'credits')">Credits &amp; plans</a>
    <a href="#account" (click)="scrollTo($event, 'account')">Account &amp; billing</a>
  </nav>

  <main>
    <div class="wrap">

      <div class="hero" id="intro">
        <div class="eyebrow">Cinematic precision at AI scale</div>
        <h1>Making videos with dAIrector</h1>
        <p>dAIrector turns a brief into a finished video. You can run the full guided pipeline, from concept to a delivered cut, or jump into the Quick Create tools to make a single character, frame, or clip on its own. This guide walks through every screen in the order you will meet it.</p>
        <div class="meta">
          <span>7-stage pipeline</span>
          <span>6 Quick Create tools</span>
          <span>Reusable Asset Library</span>
          <span>Credit-based</span>
        </div>
      </div>

      <div class="callout cyan">
        <span class="ico">i</span>
        <p>Two colours run through the whole product. <b style="color:var(--cyan)">Electric cyan</b> marks something the AI does or an action you take. <b style="color:var(--amber)">Cinema amber</b> marks a moment that needs a human: a review, an approval, or a lock. When you see amber, dAIrector is waiting on you.</p>
      </div>

      <!-- ============ SIGN IN ============ -->
      <section id="signin">
        <div class="eyebrow">Getting started</div>
        <h2>Signing in</h2>
        <p class="lead">Open the app and you land on the sign-in screen. Use a social account or your work email.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/auth/login</span></div>
            <img src="assets/images/user-guide/00-login.jpg" alt="Sign-in screen">
          </div>
          <figcaption>The sign-in screen. Left: what dAIrector does. Right: how to get in.</figcaption>
        </figure>
        <ol class="steps">
          <li>Sign in with <strong>Google</strong>, <strong>Facebook</strong>, or <strong>LinkedIn</strong>, or type your work email.</li>
          <li>With email, choose <strong>Sign In with Magic Link</strong>. dAIrector emails you a one-tap link, so there is no password to remember.</li>
          <li>Open the link and you arrive at your Command Centre.</li>
        </ol>
      </section>

      <!-- ============ FIRST RUN ============ -->
      <section id="firstrun">
        <div class="eyebrow">Getting started</div>
        <h2>The first time you log in</h2>
        <p class="lead">A brand-new account opens on a welcome screen. No campaigns, no videos yet, just a short explainer and the two ways to begin.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/dashboard</span></div>
            <img src="assets/images/user-guide/05-firstrun.jpg" alt="First-run empty dashboard">
          </div>
          <figcaption>The empty Command Centre: zeroed stats, a welcome, and a HOW IT WORKS summary (you direct, the AI produces, you review and ship).</figcaption>
        </figure>
        <p>Scroll to the Getting Started checklist: create your first campaign, invite your team, and explore the Help Center.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/dashboard</span></div>
            <img src="assets/images/user-guide/06-firstrun-start.jpg" alt="Getting started checklist">
          </div>
          <figcaption>From here you pick one of two ways to begin.</figcaption>
        </figure>
        <div class="grid">
          <div class="tool">
            <div class="th"><span class="ti">&#127916;</span><h4>Start your first campaign</h4></div>
            <p>The guided pipeline. Set your brand voice, team, and budget, then work a video from brief to finished cut, stage by stage. Best when you want a complete video.</p>
          </div>
          <div class="tool">
            <div class="th"><span class="ti">&#9889;</span><h4>Jump into Quick Create</h4></div>
            <p>Skip straight to the standalone tools and make a single character, location, frame, or clip. Everything you make lands in your Asset Library to reuse later.</p>
          </div>
        </div>
        <div class="callout cyan"><span class="ico">&#9889;</span><p>Once you have a campaign running, this welcome screen is replaced by your live <b>Command Centre</b>, covered next.</p></div>
      </section>

      <!-- ============ COMMAND CENTRE ============ -->
      <section id="command">
        <div class="eyebrow">Home base</div>
        <h2>The Command Centre</h2>
        <p class="lead">Your dashboard. It shows how much work is in flight and what is waiting on you, and it is where new campaigns begin.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/dashboard</span></div>
            <img src="assets/images/user-guide/01-dashboard.jpg" alt="Command Centre dashboard">
          </div>
          <figcaption>Top: your at-a-glance numbers. Right: everything that needs a human decision.</figcaption>
        </figure>
        <ul class="do">
          <li>The three cards up top track your whole account: <strong>Total Campaigns</strong>, <strong>Pipeline Load</strong> (videos being worked on), and <strong>Credit Velocity</strong> (how fast you are spending).</li>
          <li><strong>Human Attention Required</strong> on the right lists anything paused for your review or sign-off. Click an item to jump straight to it.</li>
          <li>The top bar always shows your <strong>credit balance</strong>, notifications, and the organisation switcher.</li>
          <li><strong>Create New Campaign</strong> starts the full guided pipeline.</li>
        </ul>

        <h3>Your campaigns, at a glance</h3>
        <p>Scroll down and each campaign expands into its videos. Every video shows its place in the seven-stage pipeline, so you can see exactly where each one is stuck or moving.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/dashboard</span></div>
            <img src="assets/images/user-guide/02-dashboard-pipeline.jpg" alt="Campaign pipeline table on the dashboard">
          </div>
          <figcaption>The numbered track (1 to 7) is the pipeline. A highlighted number is the video's current stage.</figcaption>
        </figure>
      </section>

      <!-- ============ TWO WAYS ============ -->
      <section id="twoways">
        <div class="eyebrow">The big picture</div>
        <h2>Two ways to create</h2>
        <p class="lead">Everything in dAIrector is one of these two. Knowing which you want saves a lot of clicking.</p>
        <div class="grid">
          <div class="tool">
            <div class="th"><span class="ti">&#127916;</span><h4>Campaign pipeline</h4></div>
            <p>The full journey: a brief becomes a script, a look, a storyboard, then rendered shots, sound, and a delivered video. Guided, stage by stage. Use this to make a complete video.</p>
          </div>
          <div class="tool">
            <div class="th"><span class="ti">&#9889;</span><h4>Quick Create</h4></div>
            <p>Standalone tools for one asset at a time: a character, a location, a frame, a clip, a scene, or a storyboard. Everything you make lands in your Asset Library to reuse later.</p>
          </div>
        </div>
        <div class="callout cyan"><span class="ico">&#9889;</span><p>The two work together. Characters and locations you build in Quick Create can be pulled into a campaign, and assets a campaign generates are saved back to the same library.</p></div>
      </section>

      <!-- ============ CAMPAIGN HUB ============ -->
      <section id="hub">
        <div class="eyebrow">Campaign pipeline</div>
        <h2>Campaign Hub</h2>
        <p class="lead">Every campaign you run, with its progress and health in one place.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/campaign</span></div>
            <img src="assets/images/user-guide/30-campaign-hub.jpg" alt="Campaign Hub">
          </div>
          <figcaption>Stat cards across the top, then a card per campaign with its progress bar.</figcaption>
        </figure>
        <ul class="do">
          <li>Filter with <strong>All</strong>, <strong>Active</strong>, <strong>Action Required</strong>, and <strong>Completed</strong>, or search by brand, team, or campaign ID.</li>
          <li>The <strong>+</strong> card creates a new campaign.</li>
          <li>Click any campaign card to open it.</li>
        </ul>
      </section>

      <!-- ============ CAMPAIGN DETAILS ============ -->
      <section id="details">
        <div class="eyebrow">Campaign pipeline</div>
        <h2>Inside a campaign</h2>
        <p class="lead">A campaign is an umbrella that holds several videos plus the shared brief and assets behind them.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/campaign/campaign-details</span></div>
            <img src="assets/images/user-guide/31-campaign-details.jpg" alt="Campaign details">
          </div>
          <figcaption>Counts up top, the Campaign Soul (the brief), and the credit budget on the right.</figcaption>
        </figure>
        <ul class="do">
          <li><strong>Campaign Soul</strong> is the shared brief: the product, the business goal, and the target market. Every video in the campaign inherits it.</li>
          <li><strong>Campaign Budget</strong> shows credits used against the campaign's allowance.</li>
          <li><strong>Campaign Presets</strong> hold products, logos, and brand elements reused across videos, so a look is set once, not per video.</li>
        </ul>

        <h3>The video list</h3>
        <p>Scroll to the videos. Each one carries the full seven-stage track. The completed stages are ticked, and the current stage is highlighted. Click a stage to open it.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/campaign/campaign-details</span></div>
            <img src="assets/images/user-guide/32-campaign-videos.jpg" alt="Video list with pipeline stages">
          </div>
          <figcaption>Each video: Concept Manifest &rarr; Script &rarr; Moodboard &rarr; Storyboard &rarr; Video &rarr; Sound &rarr; Preview.</figcaption>
        </figure>
      </section>

      <!-- ============ PIPELINE ============ -->
      <section id="pipeline">
        <div class="eyebrow">Campaign pipeline &middot; <b>the seven stages</b></div>
        <h2>How a video gets made</h2>
        <p class="lead">Every video moves through the same seven stages, in order. Each stage hands its approved result to the next, so a change early on flows forward. You approve the work at each gate before it continues.</p>

        <!-- Stage 1 -->
        <div class="stage" id="s-manifest">
          <div class="stage-head">
            <div class="stage-num">1</div>
            <div><h3>Concept Manifest</h3><div class="sub">The brief, agreed</div></div>
          </div>
          <p>The starting point: dAIrector turns your brief into a concept for the video and asks you to approve it. Nothing else generates until the concept is signed off, because everything downstream is built from it.</p>
          <div class="chips"><span class="pill am">needs your sign-off</span><span class="pill ok">then unlocks Script</span></div>
        </div>

        <!-- Stage 2 -->
        <div class="stage" id="s-script">
          <div class="stage-head">
            <div class="stage-num">2</div>
            <div><h3>Script</h3><div class="sub">Script Engine</div></div>
          </div>
          <p>dAIrector writes a scene-by-scene screenplay from your concept. Visual notes and sound cues are added into each scene automatically, so you never hand-write those. Review it, request changes, and approve.</p>
          <figure>
            <div class="frame">
              <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/script-screenplay</span></div>
              <img src="assets/images/user-guide/33-script-engine.jpg" alt="Script Engine">
            </div>
            <figcaption>Scenes with location, time of day, action, and sound cues. Versions sit in tabs (v1, v2).</figcaption>
          </figure>
          <ul class="do">
            <li>Every edit makes a new version. Old versions stay in the <strong>v1 / v2</strong> tabs, so nothing is lost.</li>
            <li>When it reads right, hit <strong>Approve</strong>. The status flips to "Ready for Moodboard" and the <strong>Go to Moodboard</strong> button lights up.</li>
          </ul>
        </div>

        <!-- Stage 3 -->
        <div class="stage" id="s-moodboard">
          <div class="stage-head">
            <div class="stage-num">3</div>
            <div><h3>Moodboard</h3><div class="sub">Lock the look</div></div>
          </div>
          <p>dAIrector reads the script and pulls out everything that needs a consistent look: characters, locations, objects and products, and brand elements. You build and <strong>lock</strong> a look for each one. Locking is what keeps a face or a place identical across every later shot.</p>
          <figure>
            <div class="frame">
              <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/moodboard</span></div>
              <img src="assets/images/user-guide/34-moodboard.jpg" alt="Moodboard with visual elements">
            </div>
            <figcaption>Four groups of visual elements. The counter ("4 of 5 locked") tracks how close you are to continuing.</figcaption>
          </figure>
          <div class="callout amber"><span class="ico">&#9679;</span><p><b>Locking matters.</b> An unlocked element can drift and look different from shot to shot. Lock each one to hold its look steady. You continue to Storyboard once everything is locked.</p></div>
        </div>

        <!-- Stage 4 -->
        <div class="stage" id="s-storyboard">
          <div class="stage-head">
            <div class="stage-num">4</div>
            <div><h3>Storyboard</h3><div class="sub">Script into shots</div></div>
          </div>
          <p>dAIrector breaks each scene into individual shots, choosing camera angle, shot type, and framing, and drops your locked characters, locations, and props into each one. You then refine the shots before anything is rendered.</p>
          <figure>
            <div class="frame">
              <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/shotlist-storyboard</span></div>
              <img src="assets/images/user-guide/35-storyboard-gate.jpg" alt="Storyboard board">
            </div>
            <figcaption>The storyboard: each scene broken into numbered shots with a synopsis. Open a scene to refine, generate, and approve its frames. Toggle Grid or Presentation, and set the aspect ratio.</figcaption>
          </figure>
        </div>

        <!-- Stage 5 -->
        <div class="stage" id="s-video">
          <div class="stage-head">
            <div class="stage-num">5</div>
            <div><h3>Video</h3><div class="sub">Shots become footage</div></div>
          </div>
          <p>With the storyboard approved, dAIrector reads it, infers motion and timing, times the dialogue, and builds a render queue. Each shot is generated into footage in the Video Studio.</p>
          <figure>
            <div class="frame">
              <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/production-video</span></div>
              <img src="assets/images/user-guide/36-production-video-gate.jpg" alt="Preparing shots for video synthesis">
            </div>
            <figcaption>Preparing the shots for synthesis. Approved, locked shots move into the render queue.</figcaption>
          </figure>
          <h3>The Editorial Workspace</h3>
          <p>Once the footage is ready, you choose how to finish the cut. Three ways, and you can switch between them at any time.</p>
          <figure>
            <div class="frame">
              <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/workspace-gate</span></div>
              <img src="assets/images/user-guide/39-workspace-approaches.jpg" alt="Choose your editing approach">
            </div>
            <figcaption>Hire a Pro Editor, invite your own editor, or finish it yourself with the built-in tools.</figcaption>
          </figure>
          <div class="grid">
            <div class="tool"><div class="th"><h4>Hire a Pro Editor</h4></div><p class="cost" style="font-family:var(--mono);font-size:11px;color:var(--amber)">from $99 / video</p><p>A verified editor from dAIrector's network works inside your workspace and delivers a broadcast-ready file.</p></div>
            <div class="tool"><div class="th"><h4>Invite Your Editor</h4></div><p>Send your own editor a workspace link. They get your assets, versions, and feedback tools, with no account needed.</p></div>
            <div class="tool"><div class="th"><h4>dAIrector Handles It</h4></div><p>Trim, arrange, and enhance the footage yourself with the AI-assisted tools. Best for quick turnarounds.</p></div>
          </div>
        </div>

        <!-- Stage 6 -->
        <div class="stage" id="s-sound">
          <div class="stage-head">
            <div class="stage-num">6</div>
            <div><h3>Sound</h3><div class="sub">Sonic Layer</div></div>
          </div>
          <p>Give the video its music and audio. dAIrector picks an emotional arc from your concept, and you shape the mood, generate background-music variations, and balance the mix.</p>
          <figure>
            <div class="frame">
              <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/sonic-layer</span></div>
              <img src="assets/images/user-guide/37-sonic-layer.jpg" alt="Sonic Layer sound stage">
            </div>
            <figcaption>Emotional arc and mood on the left, the track in the middle, and the mix on the right.</figcaption>
          </figure>
          <ul class="do">
            <li><strong>Generate More</strong> makes background-music variations. <strong>Upload More</strong> brings in your own track.</li>
            <li>The <strong>Mix Settings</strong> control BGM volume, ducking under voiceover, and fade in and out.</li>
          </ul>
        </div>

        <!-- Stage 7 -->
        <div class="stage" id="s-preview">
          <div class="stage-head">
            <div class="stage-num">7</div>
            <div><h3>Preview &amp; deliver</h3><div class="sub">The finished cut</div></div>
          </div>
          <p>The final stage brings the footage, voice, and music together into the finished video to preview and export. Once the cut is signed off, the video is done and the campaign marks it complete. Every asset it used stays in your library for the next video.</p>
        </div>
      </section>

      <!-- ============ ASSET LIBRARY ============ -->
      <section id="library">
        <div class="eyebrow">Quick Create</div>
        <h2>The Asset Library</h2>
        <p class="lead">Every reusable asset you make lives here: characters, locations, frames, and clips. Pull them into any video.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/generation-library</span></div>
            <img src="assets/images/user-guide/03-asset-library-empty.jpg" alt="Asset Library">
          </div>
          <figcaption>Assets are grouped by type, with a tab for each (Characters, Locations, Frames, Clips). When it is empty, the library points you at the builders.</figcaption>
        </figure>
        <p>Pick a builder to make your first asset (Character, Location, Frame, or Motion), or bring your own file with <strong>Upload your first asset</strong>. Anything you generate saves here automatically and can be pulled into any video. Use <strong>Search name or tag</strong> and <strong>Add asset</strong> in the top bar once it fills up.</p>
      </section>

      <!-- ============ BUILDERS ============ -->
      <section id="builders">
        <div class="eyebrow">Quick Create</div>
        <h2>The six builders</h2>
        <p class="lead">Every builder works the same way: describe what you want (or upload a reference), pick a model and settings, then generate. Refine, then save it to your library. The number on the Generate button is the credit cost.</p>

        <h3 id="b-character">Character Builder</h3>
        <p>Cast a consistent person you can reuse across every frame and clip. Describe them, drop in a reference photo, or set their DNA, then generate.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/character-builder</span></div>
            <img src="assets/images/user-guide/10-character-builder.jpg" alt="Character Builder">
          </div>
          <figcaption>Describe the character, choose a model and resolution, and set how many to generate.</figcaption>
        </figure>
        <ul class="do">
          <li>Choose the AI <strong>model</strong>, the <strong>resolution</strong> (1K or 2K), and how many options to make (1, 2, or 4).</li>
          <li>Add <strong>views</strong> such as Front, Left Profile, Right Profile, and Full, so the character is consistent from every angle.</li>
          <li>Refine with variations, then name it and save it to the library.</li>
        </ul>

        <h3 id="b-location">Location Builder</h3>
        <p>Build a place or set that stays the same across all your shots. Views cover Wide, Medium, and Closeup.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/location-builder</span></div>
            <img src="assets/images/user-guide/12-location-builder.jpg" alt="Location Builder">
          </div>
          <figcaption>Describe the place once and keep its look consistent everywhere it appears.</figcaption>
        </figure>

        <h3 id="b-frame">Frame Builder</h3>
        <p>Compose a single still: a shot, a character look, or a scene. Describe it and generate.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/frame-builder</span></div>
            <img src="assets/images/user-guide/11-frame-builder.jpg" alt="Frame Builder">
          </div>
          <figcaption>One AI image from a shot description. Refine and save it after.</figcaption>
        </figure>

        <h3 id="b-motion">Motion Builder</h3>
        <p>Animate a still into a short clip. Describe the motion, or upload an existing video to edit.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/motion-builder</span></div>
            <img src="assets/images/user-guide/13-motion-builder.jpg" alt="Motion Builder">
          </div>
          <figcaption>Direct the motion in words. The clip renders as a "take" you can keep or redo.</figcaption>
        </figure>

        <h3 id="b-scene">Scene Visualizer</h3>
        <p>Visualise a single moment across several shots. Describe the scene, choose a style, and pick how many shots. For a whole film, jump to the Storyboard Builder instead.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/scene-visualizer</span></div>
            <img src="assets/images/user-guide/14-scene-visualizer.jpg" alt="Scene Visualizer">
          </div>
          <figcaption>Describe the moment, set the number of shots, then visualise. Edit and animate after.</figcaption>
        </figure>

        <h3 id="b-storyboard">Storyboard Builder</h3>
        <p>Turn a whole script into a storyboard on its own. Paste or upload a script and dAIrector detects the scenes, characters, and locations, then breaks it into frames you can edit and animate.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/storyboard-builder</span></div>
            <img src="assets/images/user-guide/15-storyboard-builder.jpg" alt="Storyboard Builder">
          </div>
          <figcaption>Paste a script, choose a model, and build the storyboard. The button shows the frame count and cost.</figcaption>
        </figure>
      </section>

      <!-- ============ ORGANISATION ============ -->
      <section id="organisation">
        <div class="eyebrow">Account</div>
        <h2>Organisation</h2>
        <p class="lead">Your studio: the people on your team and their roles.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/organization-details</span></div>
            <img src="assets/images/user-guide/20-organisation.jpg" alt="Organisation">
          </div>
          <figcaption>The Personnel and Persona Hub: your org, its plan, and every member.</figcaption>
        </figure>
        <ul class="do">
          <li><strong>Invite Human Crew</strong> adds a teammate by email.</li>
          <li>The members table shows each person's role, when they were last active, and when they were invited.</li>
          <li><strong>Edit Details</strong> updates the organisation name and information.</li>
        </ul>
      </section>

      <!-- ============ CREDITS / PLANS ============ -->
      <section id="credits">
        <div class="eyebrow">Account</div>
        <h2>Credits &amp; plans</h2>
        <p class="lead">Generating costs credits. Your balance is always in the top bar, and every Generate button shows what that action will cost before you commit.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/subscription</span></div>
            <img src="assets/images/user-guide/21-subscription-hub.jpg" alt="Subscription Hub">
          </div>
          <figcaption>The Subscription Hub: your resource meter, burn rate, and current plan.</figcaption>
        </figure>
        <ul class="do">
          <li>The <strong>resource meter</strong> shows credits remaining, your renewal date, your daily burn rate, and an estimate of when you will run out.</li>
          <li><strong>Top-Up Credits</strong> adds credits now. <strong>Auto-Reload</strong> tops up automatically when you run low.</li>
        </ul>
        <h3>Choosing a plan</h3>
        <p>Compare plans on one screen, billed monthly or yearly. Yearly saves 20%.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/subscription-plans</span></div>
            <img src="assets/images/user-guide/23-subscription-plans.jpg" alt="Subscription Plans">
          </div>
          <figcaption>Free, Pro, and Studio. Your current plan is marked, and you can upgrade or downgrade anytime.</figcaption>
        </figure>
      </section>

      <!-- ============ ACCOUNT / BILLING ============ -->
      <section id="account">
        <div class="eyebrow">Account</div>
        <h2>Account &amp; billing</h2>
        <p class="lead">Your profile and your payment details.</p>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/account-setting</span></div>
            <img src="assets/images/user-guide/22-account.jpg" alt="Account settings">
          </div>
          <figcaption>Profile details: your photo and name, plus the rest of your account settings.</figcaption>
        </figure>
        <figure>
          <div class="frame">
            <div class="chrome"><span class="dot r"></span><span class="dot y"></span><span class="dot g"></span><span class="url">/layout/settings-billing</span></div>
            <img src="assets/images/user-guide/24-settings-billing.jpg" alt="Billing and payments">
          </div>
          <figcaption>Billing and Payments: the name, contact, and address used on your invoices.</figcaption>
        </figure>
      </section>

      <div class="foot">
        dAIrector User Guide &middot; generated from the live app (v11.1). Screens shown with real workspace data. Cinematic precision at AI scale.
      </div>

    </div>
  </main>
</div>
